// Pick any status for a wish (W53.1 D-5 / ADR-0177).
//
// A second sheet rather than a popover, matching `WishPhotoPickerSheet` — the
// same sheet-inside-a-sheet shape W52.1 settled on for the photo picker. A
// popover would stack a second dismissible layer on top of the bottom sheet,
// and on a 393×852 screen that is two ways out of one screen.
//
// It exists because the backend's status machine is now free in all 16
// directions. The old sheet offered one row — "the next status" — which could
// express at most `completed → pending`, so 「已完成 → 已放棄」 was unreachable
// even after the server stopped refusing it. A list of four is the honest
// amount of UI for 4×4: the current one is marked and picking it is a no-op.

import { Check } from "lucide-react";

import { BottomSheet } from "@/components/patterns/BottomSheet";
import { WISH_STATUS_LABEL, WISH_STATUS_ORDER } from "@/lib/i18n/wishlist";
import { cn } from "@/lib/utils/cn";

export interface WishStatusPickerSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  current: string;
  busy?: boolean;
  onPick: (status: (typeof WISH_STATUS_ORDER)[number]) => void;
}

export function WishStatusPickerSheet({
  open,
  onOpenChange,
  current,
  busy = false,
  onPick,
}: WishStatusPickerSheetProps) {
  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      variant="mobile"
      title="變更狀態"
      className="max-h-[60vh] overflow-y-auto"
    >
      <div data-testid="wish-status-picker">
        <ul className="flex flex-col divide-y divide-hairline">
          {WISH_STATUS_ORDER.map((status) => {
            const active = status === current;
            return (
              <li key={status}>
                <button
                  type="button"
                  disabled={busy || active}
                  onClick={() => {
                    onPick(status);
                    onOpenChange(false);
                  }}
                  className={cn(
                    // `tap-target` carries the 44px minimum every other row in
                    // this sheet relies on.
                    "flex min-h-[44px] w-full items-center justify-between px-s-4 py-s-3 text-left",
                    busy || active ? "opacity-50" : "hover:bg-black/5",
                  )}
                  aria-current={active ? "true" : undefined}
                  data-testid={`wish-status-option-${status}`}
                >
                  <span className="text-body text-ink-1">{WISH_STATUS_LABEL[status]}</span>
                  {active ? <Check size={18} className="text-you-strong" aria-hidden /> : null}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </BottomSheet>
  );
}
